import { VideoMaxAdDurationTargeting } from '@openx/types/targeting/targetingValuesTypes';
import type { AdDurationOption, MaxAdDurationStatePayload } from '@openx/types/targeting/video';
import { useCallback, useMemo } from 'react';

export const useValidation = (value: MaxAdDurationStatePayload | null) => {
  const validateStart = useCallback((start: AdDurationOption, end: AdDurationOption) => {
    if (start === null && end !== null) return { error: true, helperText: 'Value is required' };

    if (start !== null) {
      const numericValue = Number(start);

      if (Number.isNaN(numericValue)) {
        return { error: true, helperText: 'Input must be a number' };
      }

      const intValue = parseInt(start, 10);
      if (intValue < 1 || intValue > 360) {
        return { error: true, helperText: 'Value must be between 1 and 360' };
      }

      if (!Number.isInteger(numericValue) || !/^\d+$/.test(start)) {
        return { error: true, helperText: 'Input must be an integer' };
      }
    }

    return { error: false, helperText: '' };
  }, []);

  const validateEnd = useCallback((start: AdDurationOption, end: AdDurationOption) => {
    if (end !== null) {
      const isUnlimited = end === 'Unlimited';
      const numericValue = Number(end);

      if (!isUnlimited && Number.isNaN(numericValue)) {
        return { error: true, helperText: 'Input must be a number or "Unlimited"' };
      }

      if (!isUnlimited) {
        const intValue = parseInt(end, 10);
        if (intValue < 1 || intValue > 360) {
          return { error: true, helperText: 'Value must be between 1 and 360' };
        }
      }

      if (!isUnlimited && (!Number.isInteger(numericValue) || !/^\d+$/.test(end))) {
        return { error: true, helperText: 'Input must be an integer' };
      }

      if (!isUnlimited && start !== null) {
        const startInt = parseInt(start, 10);
        const endInt = parseInt(end, 10);
        if (startInt >= endInt) {
          return {
            error: true,
            helperText: 'The second value must be larger than the first one',
          };
        }
      }
    }

    return { error: false, helperText: '' };
  }, []);

  const validation = useMemo(() => {
    const start = value?.start ?? null;
    const end = value?.end ?? null;

    const startValidationResult = validateStart(start, end);
    const endValidationResult = validateEnd(start, end);

    if (value?.type === VideoMaxAdDurationTargeting.EXACT) {
      return {
        start: startValidationResult,
        end: startValidationResult,
      };
    } else {
      return {
        start: startValidationResult,
        end: endValidationResult,
      };
    }
  }, [validateStart, validateEnd, value?.start, value?.end, value?.type]);

  return { validation };
};
